import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DeviceMobile, Desktop, SignOut, ClockCounterClockwise, ShieldCheck } from "@phosphor-icons/react";

function DeviceIcon({ device }) {
  const isMobile = /iphone|ipad|android/i.test(device || "");
  const Icon = isMobile ? DeviceMobile : Desktop;
  return <Icon size={20} className="text-slate-500" />;
}

export default function Sessions() {
  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/sessions");
      setRows(r.data.items || []);
      setCurrent(r.data.current_sid || null);
    } catch {
      toast.error("Could not load sessions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const revokeOne = async (sid) => {
    if (busy) return;
    if (!window.confirm("Sign out of this device?")) return;
    setBusy(true);
    try {
      await api.delete(`/auth/sessions/${sid}`);
      toast.success("Device signed out");
      if (sid === current) {
        window.location.href = "/login";
        return;
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Sign out of every other device? Only this one will stay signed in.")) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/sessions/revoke-others");
      toast.success(`Signed out ${r.data.revoked} other device${r.data.revoked === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="sessions-page">
      <div>
        <div className="label-caps">Security</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Active sessions</h1>
        <p className="text-slate-600 mt-1">
          Every device you've signed in from. Revoke any you don't recognise, or one-tap sign out everywhere else.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-600">
          {loading ? "Loading…" : `${rows.length} active session${rows.length === 1 ? "" : "s"}`}
        </div>
        <Button
          onClick={revokeOthers}
          disabled={busy || rows.length <= 1}
          className="bg-red-600 hover:bg-red-700 h-10"
          data-testid="sessions-signout-others-btn"
        >
          <SignOut size={16} className="mr-1.5" /> Sign out everywhere else
        </Button>
      </div>

      <div className="space-y-2">
        {rows.map((s) => (
          <div key={s.id} data-testid={`session-row-${s.id}`}
               className={`card-tactile p-4 flex items-start justify-between gap-3 ${s.current ? "ring-2 ring-emerald-200 bg-emerald-50/30" : ""}`}>
            <div className="flex items-start gap-3 min-w-0">
              <DeviceIcon device={s.device} />
              <div className="min-w-0">
                <div className="font-display text-base font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                  {s.device || "Unknown device"} · <span className="text-slate-600 font-normal">{s.browser || "Browser"}</span>
                  {s.current && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold" data-testid={`session-current-${s.id}`}>
                      <ShieldCheck size={12} weight="fill" /> This device
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
                  <ClockCounterClockwise size={12} />
                  Last seen {(s.last_seen_at || "").replace("T", " ").slice(0, 16)}
                  {s.ip && <span className="ml-2 font-mono">· {s.ip}</span>}
                </div>
              </div>
            </div>
            <Button
              variant="outline" size="sm" disabled={busy}
              onClick={() => revokeOne(s.id)}
              className={`h-9 ${s.current ? "text-slate-700" : "text-red-600 border-red-200 hover:bg-red-50"}`}
              data-testid={`session-revoke-${s.id}`}
            >
              <SignOut size={14} className="mr-1.5" /> {s.current ? "Sign out this device" : "Revoke"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
